import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, FileText, Loader2, Plus, RefreshCw, X } from 'lucide-react';
import { apiService, DocumentAssetInfo } from '../services/api';

interface DocumentUploadBarProps {
  briefId?: string | null;
  sessionId?: string | null;
  ensureSessionId?: () => Promise<string | null>;
  onBusyChange?: (busy: boolean) => void;
}

const SUPPORTED_DOC_EXTENSIONS = ['.pdf', '.docx', '.doc', '.pptx', '.ppt', '.txt', '.md'];

const guessContentType = (file: File): string => {
  if (file.type) return file.type;
  const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
  const map: Record<string, string> = {
    '.pdf': 'application/pdf',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.doc': 'application/msword',
    '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    '.ppt': 'application/vnd.ms-powerpoint',
    '.txt': 'text/plain',
    '.md': 'text/markdown',
  };
  return map[ext] || 'application/octet-stream';
};

/**
 * "+ Add document" bar with per-document status chips.
 *
 * Documents are uploaded via the GCS signed-URL flow and parsed server-side;
 * status is polled from the backend so it works across instances. Rendered on
 * the landing page (before the brief starts) and inside the Brief Builder
 * panel (during the conversation).
 */
const DocumentUploadBar: React.FC<DocumentUploadBarProps> = ({
  briefId,
  sessionId,
  ensureSessionId,
  onBusyChange,
}) => {
  const [documents, setDocuments] = useState<DocumentAssetInfo[]>([]);
  const [docUpload, setDocUpload] = useState<{ name: string; percent: number } | null>(null);
  const [docError, setDocError] = useState<string | null>(null);
  const docFileInputRef = useRef<HTMLInputElement | null>(null);

  // overrideSessionId covers the first landing-page upload: the session is
  // created mid-upload, so the sessionId prop captured by this closure is
  // still null when the upload completes.
  const refreshDocuments = useCallback(
    async (overrideSessionId?: string | null) => {
      const effectiveSessionId = overrideSessionId || sessionId;
      if (!briefId && !effectiveSessionId) {
        setDocuments([]);
        return;
      }
      try {
        const result = await apiService.listDocuments({ briefId, sessionId: effectiveSessionId });
        setDocuments(result.documents || []);
      } catch {
        // Non-fatal: keep the current list.
      }
    },
    [briefId, sessionId]
  );

  useEffect(() => {
    void refreshDocuments();
  }, [refreshDocuments]);

  const docsProcessing = documents.some(
    (doc) => doc.status === 'queued' || doc.status === 'parsing'
  );
  const docsBusy = docsProcessing || docUpload !== null;

  useEffect(() => {
    if (!docsProcessing) return undefined;
    const timer = window.setInterval(() => {
      void refreshDocuments();
    }, 2500);
    return () => window.clearInterval(timer);
  }, [docsProcessing, refreshDocuments]);

  useEffect(() => {
    onBusyChange?.(docsBusy);
  }, [docsBusy, onBusyChange]);

  // Release the chat lock if this bar unmounts while a document is processing
  // (e.g. the landing bar is replaced by the Brief Builder panel's bar).
  useEffect(() => {
    return () => onBusyChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDocumentPicked = useCallback(
    async (file: File) => {
      setDocError(null);
      const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
      if (!SUPPORTED_DOC_EXTENSIONS.includes(ext)) {
        setDocError(`Unsupported file type ${ext}. Use PDF, Word, PowerPoint, or text.`);
        return;
      }
      setDocUpload({ name: file.name, percent: 0 });
      try {
        const resolvedSessionId = ensureSessionId ? await ensureSessionId() : sessionId || null;
        const contentType = guessContentType(file);
        const initiated = await apiService.initiateAttachment(
          file.name,
          contentType,
          resolvedSessionId
        );
        await apiService.uploadFileToSignedUrl(
          initiated.upload_url,
          file,
          contentType,
          (percent) => setDocUpload({ name: file.name, percent })
        );
        await apiService.completeAttachment(initiated.attachment_id, {
          sizeBytes: file.size,
          briefId,
        });
        await refreshDocuments(resolvedSessionId);
      } catch (err) {
        setDocError(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        setDocUpload(null);
      }
    },
    [briefId, ensureSessionId, refreshDocuments, sessionId]
  );

  const handleDocumentRetry = useCallback(
    async (docId: string) => {
      setDocError(null);
      try {
        await apiService.retryDocument(docId);
        await refreshDocuments();
      } catch (err) {
        setDocError(err instanceof Error ? err.message : 'Retry failed');
      }
    },
    [refreshDocuments]
  );

  const handleDocumentDelete = useCallback(async (docId: string) => {
    setDocError(null);
    try {
      await apiService.deleteDocument(docId);
      setDocuments((prev) => prev.filter((doc) => doc.doc_id !== docId));
    } catch (err) {
      setDocError(err instanceof Error ? err.message : 'Delete failed');
    }
  }, []);

  return (
    <div className="brief-docs-bar">
      <button
        type="button"
        className="brief-docs-bar__add"
        onClick={() => docFileInputRef.current?.click()}
        disabled={docUpload !== null}
        title="Upload a document (PDF, Word, PowerPoint) to inform this brief"
      >
        <Plus size={14} />
        <span>Add document</span>
      </button>
      <input
        ref={docFileInputRef}
        type="file"
        accept={SUPPORTED_DOC_EXTENSIONS.join(',')}
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleDocumentPicked(file);
          e.target.value = '';
        }}
      />
      {docUpload && (
        <span className="brief-doc-chip brief-doc-chip--uploading">
          <Loader2 size={12} className="brief-spinner" />
          <span className="brief-doc-chip__name">{docUpload.name}</span>
          <span className="brief-doc-chip__status">{docUpload.percent}%</span>
        </span>
      )}
      {documents.map((doc) => (
        <span key={doc.doc_id} className={`brief-doc-chip brief-doc-chip--${doc.status}`}>
          {doc.status === 'ready' ? (
            <CheckCircle2 size={12} />
          ) : doc.status === 'failed' || doc.status === 'stuck' ? (
            <FileText size={12} />
          ) : (
            <Loader2 size={12} className="brief-spinner" />
          )}
          <span className="brief-doc-chip__name" title={doc.filename}>
            {doc.filename}
          </span>
          {(doc.status === 'queued' || doc.status === 'parsing') && (
            <span className="brief-doc-chip__status">Processing…</span>
          )}
          {(doc.status === 'failed' || doc.status === 'stuck') && (
            <button
              type="button"
              className="brief-doc-chip__action"
              onClick={() => void handleDocumentRetry(doc.doc_id)}
              title={doc.error || 'Retry processing'}
            >
              <RefreshCw size={12} />
            </button>
          )}
          <button
            type="button"
            className="brief-doc-chip__action"
            onClick={() => void handleDocumentDelete(doc.doc_id)}
            title="Remove document"
          >
            <X size={12} />
          </button>
        </span>
      ))}
      {docsBusy && (
        <span className="brief-docs-bar__note">
          Reading document… chat resumes when it&apos;s ready
        </span>
      )}
      {docError && <span className="brief-docs-bar__error">{docError}</span>}
    </div>
  );
};

export default DocumentUploadBar;
